import React, { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Calendar, Tag, ChevronDown, ChevronRight, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import clsx from "clsx";

import { equipmentApi, projectsApi } from "@/lib/api";
import { roomColor } from "@/components/schedule/FilterBar";
import CategorySelect from "@/components/equipment/CategorySelect";

/**
 * Equipment STATUS tab. Dense table view focused on procurement state:
 *   Item · Category · Pool · Allocated · Available · Status · ETA · Vendor · Cost
 *
 * Key behaviours:
 *  • Inline-edit status (dropdown of project.equipment_statuses).
 *  • Inline-edit ETA date and vendor.
 *  • Pre-arrival warning: when ETA is set and status != last-status (i.e. not
 *    yet "arrived"), if any task scheduled in an allocated room starts before
 *    the ETA, show a red `⚠ ARRIVES LATE` chip listing the affected rooms.
 *  • Filter by status + category. Sort by ETA / status / label.
 *
 * Composes with the rest of EquipmentView via props — no own data fetch.
 */
export default function EquipmentStatusTab({
  project,
  rooms,
  tasks,
  blocks,
  equipment,
  allocations,
  categories,
  onRefresh,
}) {
  const statuses = useMemo(
    () => (project?.equipment_statuses?.length ? project.equipment_statuses : ["planned", "ordered", "shipped", "arrived"]),
    [project?.equipment_statuses]
  );
  const lastStatus = statuses[statuses.length - 1] || "arrived";

  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sortBy, setSortBy] = useState("label"); // label | eta | status

  // ── per-room earliest start lookup (used for ETA conflict detection) ──
  const earliestStartByRoom = useMemo(() => {
    const map = new Map();
    for (const b of blocks || []) {
      if (b?.deleted_at) continue;
      const s = b?.start_at;
      if (!s || !b.room_id) continue;
      const cur = map.get(b.room_id);
      if (!cur || s < cur) map.set(b.room_id, s);
    }
    return map;
  }, [blocks]);

  // ── derived rows ─────────────────────────────────────────────────
  const rows = useMemo(() => {
    const usage = new Map();
    for (const a of allocations || []) {
      if (a?.deleted_at) continue;
      usage.set(a.equipment_id, (usage.get(a.equipment_id) || 0) + (a.qty || 0));
    }
    const allocsByEq = new Map();
    for (const a of allocations || []) {
      if (a?.deleted_at) continue;
      const arr = allocsByEq.get(a.equipment_id) || [];
      arr.push(a);
      allocsByEq.set(a.equipment_id, arr);
    }
    return (equipment || [])
      .filter((e) => !e.deleted_at)
      .map((eq) => {
        const used = usage.get(eq.id) || 0;
        const available = (eq.quantity_total || 0) - used;
        const status = eq.status || statuses[0] || "planned";
        const eta = eq.eta_date || null;
        // Conflict computation: only if ETA set and not yet "arrived" (last status).
        let earlyRooms = [];
        if (eta && status !== lastStatus) {
          const eqAllocs = allocsByEq.get(eq.id) || [];
          for (const a of eqAllocs) {
            const earliestStart = earliestStartByRoom.get(a.room_id);
            if (earliestStart && earliestStart.slice(0, 10) < eta) {
              const room = rooms.find((r) => r.id === a.room_id);
              earlyRooms.push({
                room_id: a.room_id,
                room_label: room?.abbreviation || room?.name || "?",
                room_color: roomColor(room?.color_token),
                earliest_start: earliestStart,
              });
            }
          }
        }
        return { eq, used, available, status, eta, earlyRooms };
      });
  }, [equipment, allocations, statuses, lastStatus, earliestStartByRoom, rooms]);

  // ── filter + sort ────────────────────────────────────────────────
  const visibleRows = useMemo(() => {
    let r = rows;
    if (statusFilter !== "all") r = r.filter((x) => x.status === statusFilter);
    if (categoryFilter !== "all") r = r.filter((x) => (x.eq.category || "") === categoryFilter);
    if (sortBy === "label") r = r.slice().sort((a, b) => (a.eq.label || a.eq.type).localeCompare(b.eq.label || b.eq.type));
    if (sortBy === "eta") r = r.slice().sort((a, b) => (a.eta || "9999-99-99").localeCompare(b.eta || "9999-99-99"));
    if (sortBy === "status") r = r.slice().sort((a, b) => statuses.indexOf(a.status) - statuses.indexOf(b.status));
    return r;
  }, [rows, statusFilter, categoryFilter, sortBy, statuses]);

  const counts = useMemo(() => {
    const byStatus = Object.fromEntries(statuses.map((s) => [s, 0]));
    let lateCount = 0;
    for (const r of rows) {
      byStatus[r.status] = (byStatus[r.status] || 0) + 1;
      if (r.earlyRooms.length > 0) lateCount += 1;
    }
    return { byStatus, lateCount };
  }, [rows, statuses]);

  const patch = async (eqId, updates) => {
    try {
      await equipmentApi.update(eqId, updates);
      onRefresh && (await onRefresh());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Status filter strip */}
      <div className="px-3 py-2 border-b border-line bg-bg-elevated/40 flex items-center gap-2 flex-wrap" data-testid="status-filter-bar">
        <span className="mono text-2xs uppercase tracking-widest text-ink-mute mr-1">STATUS</span>
        <Chip
          active={statusFilter === "all"}
          label="ALL"
          count={rows.length}
          onClick={() => setStatusFilter("all")}
          testid="status-chip-all"
        />
        {statuses.map((s) => (
          <Chip
            key={s}
            active={statusFilter === s}
            label={s.toUpperCase()}
            count={counts.byStatus[s] || 0}
            onClick={() => setStatusFilter(s)}
            testid={`status-chip-${s}`}
          />
        ))}
        {counts.lateCount > 0 && (
          <button
            onClick={() => setSortBy("eta")}
            className="ml-auto flex items-center gap-1 mono text-2xs uppercase tracking-widest text-red border border-red px-2 py-1 hover:bg-red-glow/15"
            data-testid="status-late-summary"
            title="Items whose ETA is after their room's first task — click to sort by ETA"
          >
            <AlertTriangle className="w-3 h-3" />
            {counts.lateCount} ARRIVES LATE
          </button>
        )}
        <span className="ml-auto mono text-2xs uppercase tracking-widest text-ink-mute">CATEGORY</span>
        <Chip
          active={categoryFilter === "all"}
          label="ALL"
          onClick={() => setCategoryFilter("all")}
          testid="status-cat-all"
        />
        {categories.map((c) => (
          <Chip
            key={c.id}
            active={categoryFilter === c.name}
            label={c.name.toUpperCase()}
            onClick={() => setCategoryFilter(c.name)}
            testid={`status-cat-${c.name}`}
          />
        ))}
        <span className="mono text-2xs uppercase tracking-widest text-ink-mute ml-2">SORT</span>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="bg-bg-base border border-line-strong text-ink mono text-2xs uppercase tracking-widest px-2 py-1"
          data-testid="status-sort"
        >
          <option value="label">A→Z</option>
          <option value="eta">ETA</option>
          <option value="status">STATUS</option>
        </select>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-bg-elevated border-b border-line-strong z-10">
            <tr className="text-ink-mute mono text-2xs uppercase tracking-widest">
              <th className="text-left px-3 py-2">ITEM</th>
              <th className="text-left px-2 py-2">CATEGORY</th>
              <th className="text-right px-2 py-2">POOL</th>
              <th className="text-right px-2 py-2">USED</th>
              <th className="text-right px-2 py-2">AVAIL</th>
              <th className="text-left px-2 py-2">STATUS</th>
              <th className="text-left px-2 py-2">ETA</th>
              <th className="text-left px-2 py-2">VENDOR</th>
              <th className="text-right px-2 py-2">COST</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-12 mono text-2xs uppercase tracking-widest text-ink-mute italic text-center">
                — no equipment matches —
              </td></tr>
            )}
            {visibleRows.map(({ eq, used, available, status, eta, earlyRooms }) => (
              <StatusRow
                key={eq.id}
                eq={eq}
                used={used}
                available={available}
                status={status}
                eta={eta}
                earlyRooms={earlyRooms}
                statuses={statuses}
                categories={categories}
                onPatch={(updates) => patch(eq.id, updates)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────

function Chip({ active, label, count, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={clsx(
        "mono text-2xs uppercase tracking-widest px-2 py-1 border transition-colors flex items-center gap-1",
        active ? "border-amber text-amber bg-amber-glow/15" : "border-line text-ink-mute hover:text-ink"
      )}
    >
      {label}
      {typeof count === "number" && <span className="opacity-70 tabular-nums">{count}</span>}
    </button>
  );
}

const STATUS_COLORS = {
  planned: { ring: "border-ink-mute", dot: "bg-ink-mute" },
  ordered: { ring: "border-amber", dot: "bg-amber" },
  shipped: { ring: "border-cyan", dot: "bg-cyan" },
  arrived: { ring: "border-emerald-400", dot: "bg-emerald-400" },
};

function StatusRow({ eq, used, available, status, eta, earlyRooms, statuses, categories, onPatch }) {
  const shortfall = available < 0;
  const stStyle = STATUS_COLORS[status] || STATUS_COLORS.planned;
  const isLate = earlyRooms.length > 0;

  return (
    <tr className={clsx(
      "border-b border-line group hover:bg-bg-elevated/60 align-top transition-colors",
      isLate && "bg-red-glow/5"
    )}>
      <td className="px-3 py-2">
        <div className="text-ink truncate" title={eq.label || eq.type}>{eq.label || eq.type}</div>
        {isLate && (
          <div className="mt-0.5 flex items-center gap-1 mono text-2xs uppercase tracking-widest text-red" title={`Tasks scheduled before ETA in: ${earlyRooms.map((r) => r.room_label).join(", ")}`}>
            <AlertTriangle className="w-3 h-3" />
            ARRIVES LATE · {earlyRooms.slice(0, 3).map((r) => r.room_label).join(", ")}
            {earlyRooms.length > 3 && <span>+{earlyRooms.length - 3}</span>}
          </div>
        )}
      </td>
      <td className="px-2 py-2">
        <CategoryEdit
          value={eq.category || ""}
          categories={categories}
          onChange={(v) => onPatch({ category: v })}
          testid={`status-row-cat-${eq.id}`}
        />
      </td>
      <td className="px-2 py-2 text-right mono tabular-nums text-ink-mute">{eq.quantity_total}</td>
      <td className="px-2 py-2 text-right mono tabular-nums text-ink-mute">{used}</td>
      <td className={clsx("px-2 py-2 text-right mono tabular-nums", shortfall ? "text-red font-semibold" : "text-cyan")}>
        {shortfall ? `${available} SHORT` : available}
      </td>
      <td className="px-2 py-2">
        <select
          value={status}
          onChange={(e) => onPatch({ status: e.target.value })}
          className={clsx(
            "mono text-2xs uppercase tracking-widest px-1.5 py-0.5 bg-bg-base border outline-none",
            stStyle.ring, "focus:border-amber"
          )}
          data-testid={`status-row-status-${eq.id}`}
        >
          {statuses.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
        </select>
      </td>
      <td className="px-2 py-2">
        <InlineDate
          value={eta}
          onChange={(v) => onPatch({ eta_date: v })}
          testid={`status-row-eta-${eq.id}`}
        />
      </td>
      <td className="px-2 py-2">
        <InlineText
          value={eq.vendor || ""}
          placeholder="—"
          onChange={(v) => onPatch({ vendor: v })}
          testid={`status-row-vendor-${eq.id}`}
        />
      </td>
      <td className="px-2 py-2 text-right mono tabular-nums text-ink-mute">
        {eq.cost_amount != null ? `${eq.cost_currency || "USD"} ${eq.cost_amount.toLocaleString(undefined, { minimumFractionDigits: 0 })}` : "—"}
      </td>
    </tr>
  );
}

function CategoryEdit({ value, categories, onChange, testid }) {
  // Project-managed dropdown. Falls back to "— UNCATEGORIZED —" for ""
  return (
    <CategorySelect
      value={value}
      categories={categories.map((c) => (typeof c === "string" ? c : c.name))}
      onChange={onChange}
      buttonClassName="max-w-[140px]"
      menuWidth={220}
      testid={testid}
    />
  );
}

function InlineDate({ value, onChange, testid }) {
  const [local, setLocal] = useState(value || "");
  useEffect(() => setLocal(value || ""), [value]);
  return (
    <input
      type="date"
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => { if (local !== (value || "")) onChange(local || ""); }}
      className="bg-bg-base border border-line text-ink mono text-2xs px-1.5 py-0.5 focus:border-amber outline-none w-[120px]"
      data-testid={testid}
    />
  );
}

function InlineText({ value, placeholder, onChange, testid }) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  return (
    <input
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => { if (local !== value) onChange(local); }}
      placeholder={placeholder}
      className="bg-bg-base border border-line text-ink mono text-2xs px-1.5 py-0.5 focus:border-amber outline-none w-full max-w-[160px]"
      data-testid={testid}
    />
  );
}

// ─────────────────────────────────────────────────────────────────
// Categories manager (separate floating modal)
// ─────────────────────────────────────────────────────────────────

export function CategoriesManagerDialog({ open, onClose, projectId, categories, onMutated }) {
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);

  if (!open) return null;

  const add = async () => {
    const n = newName.trim();
    if (!n) return;
    try {
      await equipmentApi.createCategory(projectId, n);
      setNewName("");
      onMutated && (await onMutated());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Add failed");
    }
  };

  return (
    <div className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="cats-manager-overlay">
      <div className="bg-bg-elevated border border-amber min-w-[420px] max-w-[480px] w-full" onClick={(e) => e.stopPropagation()} data-testid="cats-manager-dialog">
        <header className="px-3 py-2 border-b border-line-strong flex items-center justify-between">
          <span className="mono text-2xs uppercase tracking-[0.2em] text-amber">EQUIPMENT CATEGORIES</span>
          <button onClick={onClose} className="text-ink-mute hover:text-ink" data-testid="cats-manager-close">×</button>
        </header>
        <div className="p-3 space-y-2 max-h-[60vh] overflow-y-auto">
          {categories.length === 0 && (
            <div className="mono text-2xs uppercase tracking-widest text-ink-mute italic text-center py-6">
              — no categories yet —
            </div>
          )}
          {categories.map((cat) => (
            <CategoryRow
              key={cat.id}
              category={cat}
              editing={editingId === cat.id}
              onStartEdit={() => setEditingId(cat.id)}
              onCancelEdit={() => setEditingId(null)}
              onSaveRename={async (name) => {
                try {
                  await equipmentApi.renameCategory(cat.id, name);
                  setEditingId(null);
                  onMutated && (await onMutated());
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Rename failed");
                }
              }}
              onDelete={async () => {
                try {
                  await equipmentApi.removeCategory(cat.id, true);
                  onMutated && (await onMutated());
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Delete failed");
                }
              }}
            />
          ))}
        </div>
        <div className="p-3 border-t border-line-strong">
          <div className="mono text-2xs uppercase tracking-widest text-ink-mute mb-1">+ NEW CATEGORY</div>
          <div className="flex gap-1.5">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
              placeholder="e.g. Lighting"
              className="flex-1 bg-bg-base border border-line-strong text-ink mono text-xs px-2 py-1 focus:border-amber outline-none"
              data-testid="cats-manager-new-input"
              autoFocus
            />
            <button onClick={add} disabled={!newName.trim()} className="btn-amber px-3 py-1 mono text-2xs uppercase tracking-widest disabled:opacity-40" data-testid="cats-manager-add">
              <Plus className="w-3 h-3 inline mr-1" /> ADD
            </button>
          </div>
          <div className="mono text-2xs uppercase tracking-widest text-ink-mute mt-2 opacity-70">
            Renaming a category updates every item using it. Deleting clears it from items.
          </div>
        </div>
      </div>
    </div>
  );
}

function CategoryRow({ category, editing, onStartEdit, onCancelEdit, onSaveRename, onDelete }) {
  const [draft, setDraft] = useState(category.name);
  useEffect(() => { if (editing) setDraft(category.name); }, [editing, category.name]);

  if (editing) {
    return (
      <div className="flex items-center gap-2 p-2 border border-amber bg-bg-base">
        <Tag className="w-3 h-3 text-amber" />
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); onSaveRename(draft); }
            else if (e.key === "Escape") { e.preventDefault(); onCancelEdit(); }
          }}
          className="flex-1 bg-bg-elevated border border-amber text-ink mono text-xs px-2 py-1 outline-none"
          autoFocus
          data-testid={`cats-edit-input-${category.id}`}
        />
        <button onClick={() => onSaveRename(draft)} className="btn-amber px-2 py-1 mono text-2xs uppercase tracking-widest" data-testid={`cats-edit-save-${category.id}`}>SAVE</button>
        <button onClick={onCancelEdit} className="text-ink-mute hover:text-ink mono text-2xs uppercase tracking-widest">×</button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 p-2 border border-line bg-bg-base hover:border-line-strong group" data-testid={`cats-row-${category.id}`}>
      <Tag className="w-3 h-3 text-ink-mute group-hover:text-amber transition-colors" />
      <span className="text-sm text-ink flex-1">{category.name}</span>
      <button onClick={onStartEdit} className="opacity-0 group-hover:opacity-100 btn-ghost px-2 py-0.5 transition-opacity" data-testid={`cats-rename-${category.id}`}>RENAME</button>
      <button onClick={onDelete} className="opacity-0 group-hover:opacity-100 text-ink-mute hover:text-red p-0.5 transition-opacity" data-testid={`cats-delete-${category.id}`}><Trash2 className="w-3 h-3" /></button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Statuses customisation dialog (project.equipment_statuses)
// ─────────────────────────────────────────────────────────────────

export function StatusesManagerDialog({ open, onClose, project, onMutated }) {
  const [list, setList] = useState(project?.equipment_statuses || ["planned", "ordered", "shipped", "arrived"]);
  const [newName, setNewName] = useState("");

  useEffect(() => {
    if (open) setList(project?.equipment_statuses || ["planned", "ordered", "shipped", "arrived"]);
  }, [open, project?.equipment_statuses]);

  if (!open) return null;

  const remove = (i) => setList((l) => l.filter((_, j) => j !== i));
  const move = (i, dir) => setList((l) => {
    const j = i + dir;
    if (j < 0 || j >= l.length) return l;
    const copy = l.slice();
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });
  const add = () => {
    const v = newName.trim().toLowerCase();
    if (!v || list.includes(v)) return;
    setList((l) => [...l, v]);
    setNewName("");
  };
  const save = async () => {
    if (list.length < 1) {
      toast.error("Must have at least one status.");
      return;
    }
    try {
      await projectsApi.update(project.id, { equipment_statuses: list });
      onMutated && (await onMutated());
      toast.success("Statuses saved");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="statuses-manager-overlay">
      <div className="bg-bg-elevated border border-amber min-w-[420px] max-w-[480px] w-full" onClick={(e) => e.stopPropagation()} data-testid="statuses-manager-dialog">
        <header className="px-3 py-2 border-b border-line-strong flex items-center justify-between">
          <span className="mono text-2xs uppercase tracking-[0.2em] text-amber">EQUIPMENT STATUS WORKFLOW</span>
          <button onClick={onClose} className="text-ink-mute hover:text-ink">×</button>
        </header>
        <div className="p-3 space-y-2">
          <div className="mono text-2xs uppercase tracking-widest text-ink-mute mb-1">
            Order matters — first = default for new items. Last = "arrived" (clears late-warning).
          </div>
          {list.map((s, i) => (
            <div key={s} className="flex items-center gap-2 p-2 border border-line bg-bg-base">
              <span className="mono text-2xs tabular-nums text-ink-mute w-6">{i + 1}.</span>
              <span className="text-sm text-ink flex-1 uppercase mono tracking-widest">{s}</span>
              <button disabled={i === 0} onClick={() => move(i, -1)} className="btn-ghost px-1.5 py-0.5 disabled:opacity-30" data-testid={`status-up-${s}`}>↑</button>
              <button disabled={i === list.length - 1} onClick={() => move(i, 1)} className="btn-ghost px-1.5 py-0.5 disabled:opacity-30" data-testid={`status-down-${s}`}>↓</button>
              <button onClick={() => remove(i)} disabled={list.length <= 1} className="text-ink-mute hover:text-red p-0.5 disabled:opacity-30" data-testid={`status-remove-${s}`}><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
          <div className="flex gap-1.5 pt-1">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
              placeholder="e.g. on_site"
              className="flex-1 bg-bg-base border border-line-strong text-ink mono text-xs px-2 py-1 focus:border-amber outline-none"
              data-testid="status-new-input"
            />
            <button onClick={add} disabled={!newName.trim()} className="btn-ghost px-3 py-1 mono text-2xs uppercase tracking-widest disabled:opacity-40" data-testid="status-add">
              <Plus className="w-3 h-3 inline mr-1" /> ADD
            </button>
          </div>
        </div>
        <div className="p-3 border-t border-line-strong flex justify-end gap-1">
          <button onClick={onClose} className="btn-ghost px-3 py-1.5 mono text-2xs uppercase tracking-widest">CANCEL</button>
          <button onClick={save} className="btn-amber px-3 py-1.5 mono text-2xs uppercase tracking-widest" data-testid="statuses-save">SAVE</button>
        </div>
      </div>
    </div>
  );
}
